import type { IServerValidationError } from '../components/Shared/validatable.interface';
import type IValidatable from '../components/Shared/validatable.interface';
import type ValidationInfo from '../validation/validation-info';

export type ValidatableComponent = {
  component: IValidatable;
  validationInfo: ValidationInfo;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setState: React.Dispatch<React.SetStateAction<{ value: any; validationInfo: ValidationInfo }>>;
};

export class ValidationHelper {
  public static scrollToAndFocusOnValidationSummary(): void {
    const element = document.querySelector('.validation-summary.has-validation-errors') as HTMLElement;
    if (element) {
      element.scrollIntoView();
      element.focus();
    }
  }

  public static checkValidity(validatableComponents: ValidatableComponent[]): [boolean, ValidationInfo[]] {
    const invalidValidationInfos: ValidationInfo[] = [];
    const isFormValid = validatableComponents.reduce((acc, curr) => {
      const validationInfo = curr.component.checkValidity();
      curr.setState((ps) => ({ ...ps, validationInfo }));
      if (!validationInfo.isValid) {
        invalidValidationInfos.push(validationInfo);
      }
      return acc && validationInfo.isValid;
    }, true);
    return [isFormValid, invalidValidationInfos];
  }

  public static handleServerValidationErrors(errors: IServerValidationError[], validatableComponents: ValidatableComponent[]): ValidationInfo[] {
    const invalidValidationInfos: ValidationInfo[] = [];
    validatableComponents.forEach((validatableComponent) => {
      const validationInfo = validatableComponent.component.handleServerValidationErrors(errors);
      validatableComponent.setState((ps) => ({ ...ps, validationInfo }));
      if (!validationInfo.isValid) {
        invalidValidationInfos.push(validationInfo);
      }
    });
    return invalidValidationInfos;
  }

  public static getValidationSummaryErrors(validatableComponents: { validationInfo: ValidationInfo }[]): string[] {
    function recurseChildren(children: { [key: string]: ValidationInfo }): string[] {
      const result: string[] = [];
      Object.values(children).forEach((child) => {
        result.push(...child.getErrorsWithPrefix());
        result.push(...recurseChildren(child.children));
      });
      return result;
    }

    return validatableComponents.reduce((acc, curr) => acc.concat(curr.validationInfo.getErrorsWithPrefix()).concat(recurseChildren(curr.validationInfo.children)), [] as string[]);
  }

  public static trackValidationErrors(validationInfos: ValidationInfo[], func: (e: string) => void): void {
    validationInfos
      .reduce((acc, val) => acc.concat(val), [] as ValidationInfo[])
      .filter((vi) => vi.isInvalid)
      .reduce((acc, val) => acc.concat(val.getErrorsWithPrefix()), [] as string[])
      .forEach((error) => {
        func(error);
      });
  }
}
